/**
 * The in-page header (TMMAPP-0072, TMM-B7; TMMAPP-0099; the v2 header, TMMAPP-0150 U2, visual
 * contract v2 §4): a back link or an eyebrow, the page's one `h1` with its kebab beside it, the
 * lead, a chip row and, on the right, aligned to the bottom, the page's actions and headline figure.
 * The v2 top bar shows no title at any width, so this is where a page says what it is.
 *
 * - `h1` is `id="page-title"` with `tabIndex={-1}`: route focus lands on it (`RouteFocus`).
 * - `title: null` is the loading state of a title that depends on data: a skeleton in the `h1`
 *   (still the page's one heading, labelled "Loading…"), so every state has exactly one `h1`.
 *   When the title resolves while route focus is still on that `h1`, the header announces it once
 *   through its own polite live region (TMMAPP-0081); a title that was there at mount, and a later
 *   change of a resolved title, announce nothing.
 * - `back` is a detail page's "← {parent}" link in place of the eyebrow, above the `h1` (v2 §4:
 *   13 / 600, `link`, a 6 px gap, sentence case). Breadcrumbs are not rendered (TMM-B7 1.4).
 * - The eyebrow is one rule, not a prop (§B 11): "Money Tools" on a tool page (`activeTool`) with no
 *   `back`, else none — 13 / 400 in the page's label ink, sentence case.
 * - `menu` is the page kebab, beside the `h1` (8px gap), in the `page` menu style.
 * - The row is flex, aligned to the end, gap 16 / 24, and wraps. The left block holds the back link
 *   or the eyebrow, the `h1` and its kebab, `meta` (the share line), the `subtitle` — the lead, 15 in
 *   `text2`, 6 px under the title, at most 620 wide — and the `status` chip row 12 px under them. The right block holds the `actions` (the one primary action first) and the
 *   `headline`: an `overline` label over a figure the caller renders (`figureLg`, `text` or
 *   `moneyNegative`). The polite live region stays visually hidden between the h1 and the menu so
 *   a slow title is still announced once.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { Box, Button, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resolveTokens } from '@/theme/theme';
import { activeTool } from './navigation';

/** A detail page's way up: "← {label}" to `to`. */
export interface BackLink {
  label: string;
  to: string;
}

export interface PageHeaderProps {
  /** The `h1`; `null` while the title loads. */
  title: string | null;
  subtitle?: ReactNode | undefined;
  /** A detail page's back link, only where the prototype draws one. */
  back?: BackLink | undefined;
  actions?: ReactNode | undefined;
  /** Chips after the subtitle (a status, an audience badge). */
  status?: ReactNode | undefined;
  /** The page kebab, beside the h1. */
  menu?: ReactNode | undefined;
  /** A line under the h1 and before `subtitle` (the share line). */
  meta?: ReactNode | undefined;
  /** A quiet line under the subtitle and before the status chips. */
  detail?: ReactNode | undefined;
  /** Label and figure at the right. Below `md` it wraps under the title block. */
  headline?: { label: ReactNode; value: ReactNode } | undefined;
  /** Where the title block and the right block meet vertically; `start` for a tall right block (default `end`). */
  align?: 'start' | 'end' | undefined;
}

/** The "← {parent}" link (visual contract v2 §4): 13 / 600, `link`, a 6 px gap. */
function BackLinkButton({ back }: { back: BackLink }) {
  const tokens = resolveTokens(useTheme());
  return (
    <Button
      component={RouterLink}
      to={back.to}
      variant="text"
      data-back-link=""
      sx={{
        alignSelf: 'flex-start',
        minHeight: 0,
        minWidth: 0,
        gap: '6px',
        p: '2px 0',
        mb: '2px',
        fontSize: 13,
        lineHeight: '20px',
        fontWeight: 600,
        letterSpacing: 0,
        textTransform: 'none',
        color: tokens.link,
        // Under the pointer an underline, not a wash: the link has no padding to carry one.
        '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
      }}
    >
      <span aria-hidden>←</span> {back.label}
    </Button>
  );
}

export function PageHeader({
  title,
  subtitle,
  back,
  actions,
  status,
  menu,
  meta,
  detail,
  headline,
  align = 'end',
}: PageHeaderProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const eyebrow = back === undefined && activeTool(pathname) !== null ? t('nav.tools') : null;
  const loading = title === null;
  const h1Ref = useRef<HTMLHeadingElement>(null);
  const live = useRef<HTMLSpanElement>(null);
  // This header mounted on its loading skeleton: route focus (if it landed) is on an `h1` that
  // reads "Loading…", and the real title replaces it silently.
  const awaitingTitle = useRef(loading);

  // Announce the resolved title once (a polite live region this header owns) — only when route
  // focus is still on the heading, so nothing fights an `ErrorState` or a dialog that took focus,
  // and never for a later title change (a rename is not a navigation). The text is written into
  // the region directly: it is announcement plumbing, not rendered state.
  useEffect(() => {
    if (!awaitingTitle.current || title === null) return;
    awaitingTitle.current = false;
    if (document.activeElement === h1Ref.current && live.current !== null) {
      live.current.textContent = title;
    }
  }, [title]);

  const has = (node: ReactNode) => node !== undefined && node !== null && node !== '';
  const right = has(actions) || headline !== undefined;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }} data-page-header="">
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: align === 'start' ? 'flex-start' : 'flex-end',
          rowGap: 2,
          columnGap: 3,
        }}
      >
        <Box sx={{ flex: '1 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {back !== undefined && <BackLinkButton back={back} />}
          {eyebrow !== null && (
            <Typography
              variant="eyebrow"
              sx={{
                fontSize: 13,
                lineHeight: '20px',
                fontWeight: 400,
                letterSpacing: 0,
                textTransform: 'none',
              }}
            >
              {eyebrow}
            </Typography>
          )}
          <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1, mt: '2px' }}>
            <Typography
              variant="h1"
              id="page-title"
              ref={h1Ref}
              tabIndex={-1}
              {...(loading ? { 'aria-label': t('common.loading'), 'aria-busy': true } : {})}
              sx={{ minWidth: 0, overflowWrap: 'anywhere', '&:focus': { outline: 'none' } }}
            >
              {loading ? <Skeleton aria-hidden width={240} /> : title}
            </Typography>
            <Box component="span" role="status" aria-live="polite" ref={live} sx={visuallyHidden} />
            {has(menu) && (
              // The kebab sits on the h1's first line, not the middle of a wrapped title (0097 B16):
              // a box one h1 line tall, at the top of the row, centres it on that line.
              <Box
                data-page-menu=""
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: (theme) => theme.typography.h1.lineHeight,
                }}
              >
                {menu}
              </Box>
            )}
          </Stack>
          {has(meta) && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {meta}
            </Typography>
          )}
          {has(subtitle) && (
            <Typography
              variant="body1"
              data-page-lead=""
              sx={{ mt: '6px', color: (theme) => resolveTokens(theme).text2, maxWidth: 620 }}
            >
              {subtitle}
            </Typography>
          )}
          {has(detail) && <Box sx={{ mt: 0.5 }}>{detail}</Box>}
          {has(status) && (
            <Stack direction="row" sx={{ flexWrap: 'wrap', alignItems: 'center', gap: 1, mt: 1.5 }}>
              {status}
            </Stack>
          )}
        </Box>
        {right && (
          <Stack
            data-page-header-right=""
            sx={{
              flexShrink: 0,
              minWidth: 0,
              maxWidth: '100%',
              alignItems: { xs: 'flex-start', md: 'flex-end' },
              textAlign: { xs: 'start', md: 'end' },
              gap: 1.5,
            }}
          >
            {headline !== undefined && (
              <Box data-page-headline="">
                <Typography variant="overline" component="p">
                  {headline.label}
                </Typography>
                {headline.value}
              </Box>
            )}
            {has(actions) && (
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                  justifyContent: { md: 'flex-end' },
                }}
              >
                {actions}
              </Stack>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

/**
 * The `h1` of a screen that has no visible heading of its own (a sign-in interstitial, a spinner,
 * the `/me` blocking screen): visually hidden, still the page's one `h1`, still a focus target.
 */
export function HiddenPageTitle({ title }: { title: string }) {
  return (
    <Typography
      variant="h1"
      id="page-title"
      tabIndex={-1}
      sx={{ ...visuallyHidden, '&:focus': { outline: 'none' } }}
    >
      {title}
    </Typography>
  );
}
